import { DocumentPlaceholderIcon } from '@diligentcorp/atlas-react-icons';
import { AttachmentResponseDto } from '@issuemanager/api-client';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import { bytesToKB } from '@issuemanager/apps/client/utils';
import { Divider, Grid, Stack, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { useDownloadAttachment, useUserDateRenderer } from '../../hooks';

type Props = {
  files: AttachmentResponseDto[];
  showVerticalDividers?: boolean;
};

const ClickableStack = styled(Stack)`
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export function FileList({ files, showVerticalDividers = true }: Props) {
  const semantic = useAtlasSemanticThemeTokens();
  const dateRenderer = useUserDateRenderer();
  const { t } = useTranslation('common');
  const downloadAttachment = useDownloadAttachment();
  const handleDownloadAttachment = (file: AttachmentResponseDto): void => {
    downloadAttachment({
      hbDriveId: file.hbDriveId,
      name: file.name,
    });
  };

  return (
    <Grid container>
      {files.map((file, index) => (
        <Fragment key={file.id}>
          <Grid item xs={(showVerticalDividers ? 11.75 : 11.5) / 2}>
            <ClickableStack
              direction='row'
              alignItems='center'
              p={1}
              bgcolor={semantic.color.background.container.value}
              onClick={() => handleDownloadAttachment(file)}
            >
              <Stack direction='row' spacing={2} alignItems='center'>
                <DocumentPlaceholderIcon
                  fontSize={semantic.iconSize.lg.value}
                />
                <Stack>
                  <Typography
                    fontWeight={semantic.fontWeight.emphasis.value}
                    lineHeight={semantic.lineHeight.text.body.value}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    color={semantic.color.text.muted.value}
                    lineHeight={semantic.lineHeight.text.body.value}
                  >
                    {bytesToKB(file.size)}
                  </Typography>
                  <Typography
                    color={semantic.color.text.muted.value}
                    lineHeight={semantic.lineHeight.text.body.value}
                  >
                    {`${t('attachments.uploaded')} ${dateRenderer.renderDate(
                      file.createdAt,
                    )}`}
                  </Typography>
                </Stack>
              </Stack>
            </ClickableStack>
            <Divider />
          </Grid>
          {index % 2 === 0 && (
            <Grid
              item
              xs={showVerticalDividers ? 0.25 : 0.5}
              display='flex'
              justifyContent='center'
            >
              {showVerticalDividers && (
                <Divider orientation='vertical' flexItem />
              )}
            </Grid>
          )}
        </Fragment>
      ))}
    </Grid>
  );
}
